// Cap serialized values so a large object/string can't bloat the log payload.
const MAX_LENGTH = 1000;

/** Cross-realm-safe Error check (an Error thrown from an iframe fails `instanceof`). */
function isErrorLike(value: unknown): value is Error {
  return value instanceof Error || Object.prototype.toString.call(value) === '[object Error]';
}

/** Truncate to MAX_LENGTH UTF-16 units, dropping a trailing lone high surrogate
 * so the cut never leaves half of a split surrogate pair. */
function truncate(value: string): string {
  if (value.length <= MAX_LENGTH) {
    return value;
  }
  const sliced = value.slice(0, MAX_LENGTH);
  const lastCode = sliced.codePointAt(sliced.length - 1) ?? 0;
  return lastCode >= 0xd8_00 && lastCode <= 0xdb_ff ? sliced.slice(0, -1) : sliced;
}

function rawStringify(value: unknown): string {
  if (isErrorLike(value)) {
    return value.message;
  }
  if (typeof value === 'string') {
    return value;
  }

  try {
    const json = JSON.stringify(value);
    if (typeof json === 'string') {
      return json;
    }
  } catch {
    // circular reference / BigInt — fall through to String()
  }

  try {
    return String(value);
  } catch {
    // e.g. an object whose Symbol.toPrimitive throws
    return '[unserializable]';
  }
}

/**
 * Turn an arbitrary thrown/logged value into a useful, bounded log message.
 *
 * - `Error` (incl. cross-realm) → its `message` (callers forward `name`/`stack`
 *   separately).
 * - `string` → itself.
 * - anything else → `JSON.stringify` so a plain object serializes to its actual
 *   content instead of the useless `"[object Object]"` that `String()` yields.
 *
 * Falls back to `String(value)` for values JSON can't encode (circular refs,
 * `BigInt`, top-level `undefined`), and to `"[unserializable]"` if even that
 * throws. The result is always length-capped. Never throws.
 */
export function stringifyLogValue(value?: unknown): string {
  return truncate(rawStringify(value));
}
